/**
 * ELK Layout Utility for Compound Graphs
 *
 * Uses ELK.js (Eclipse Layout Kernel) for hierarchical/compound graph layout.
 * Supports parent-child relationships with proper nesting.
 *
 * Handles both:
 * - FLAT format: nodes with parent_id references (canonical format per architecture)
 * - NESTED format: nodes with children arrays (legacy format)
 */

import ELK from 'elkjs/lib/elk.bundled.js';
import type { ElkNode } from 'elkjs';
import { LogicalNode, LogicalEdge, ChildEdge, ProcessNode, ProcessEdge } from './types';

const elk = new ELK();

// Layout configuration for compound graphs
const ELK_OPTIONS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',
  'elk.spacing.nodeNode': '60',
  'elk.layered.spacing.nodeNodeBetweenLayers': '80',
  'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
  'elk.padding': '[top=40,left=40,bottom=40,right=40]',
};

// Layout options optimized for icon+type-name GraphNode (~100px wide, ~100px tall with label)
// TOP-TO-BOTTOM (waterfall) flow for logic graphs per design standards
const ELK_OPTIONS_MINIMAL = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',  // Waterfall: top-to-bottom flow
  'elk.spacing.nodeNode': '80',  // Horizontal spacing between nodes in same layer
  'elk.layered.spacing.nodeNodeBetweenLayers': '100',  // Vertical spacing between layers
  'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',  // Better for complex graphs
  'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
  'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
  'elk.separateConnectedComponents': 'false',  // Keep all nodes together
  'elk.padding': '[top=40,left=40,bottom=40,right=40]',
};

// Layout options for children inside collapsed groups
// Flow DOWN (vertically) within groups for better readability
const ELK_OPTIONS_GROUP_CHILDREN = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',  // Top-to-bottom flow for children in groups
  'elk.spacing.nodeNode': '30',  // Horizontal spacing between nodes in same layer
  'elk.layered.spacing.nodeNodeBetweenLayers': '50',  // Vertical spacing between layers
  'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
  'elk.separateConnectedComponents': 'false',
  'elk.padding': '[top=30,left=30,bottom=30,right=30]',
};

// Node dimensions for layout calculation
const NODE_WIDTH = 280;
const NODE_HEIGHT = 120;

// Dimensions for icon+type-name GraphNode (rounded box + label below)
const MINIMAL_NODE_WIDTH = 120;
const MINIMAL_NODE_HEIGHT = 100;

const GROUP_PADDING = 60; // Extra padding for group nodes

interface FlattenedNode {
  node: LogicalNode;
  parentId?: string;
  depth: number;
}

/**
 * FlattenedNode for flat format (ProcessNode with parent_id)
 */
interface FlattenedProcessNode {
  node: ProcessNode;
  parentId: string | null;
  depth: number;
}

interface LayoutResult {
  positions: Record<string, { x: number; y: number }>;
  dimensions: Record<string, { width: number; height: number }>;
  parentMap: Record<string, string | undefined>;
}

/**
 * Flatten the hierarchical node structure into a flat list with parent references.
 * This preserves parent-child relationships for React Flow's parentId feature.
 */
function flattenHierarchy(
  nodes: LogicalNode[],
  parentId?: string,
  depth: number = 0
): FlattenedNode[] {
  const result: FlattenedNode[] = [];

  nodes.forEach((node) => {
    result.push({ node, parentId, depth });

    // Recursively flatten children
    if (node.children && node.children.length > 0) {
      result.push(...flattenHierarchy(node.children, node.node_id, depth + 1));
    }
  });

  return result;
}

/**
 * Build ELK graph structure from logical nodes and edges.
 * Handles nested children by creating compound nodes.
 */
function buildElkGraph(
  nodes: LogicalNode[],
  edges: LogicalEdge[]
): ElkNode {
  // Recursive function to convert LogicalNode to ElkNode
  function convertNode(node: LogicalNode): ElkNode {
    const elkNode: ElkNode = {
      id: node.node_id,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
    };

    // If node has children, add them as nested nodes
    if (node.children && node.children.length > 0) {
      elkNode.children = node.children.map(convertNode);
      elkNode.layoutOptions = {
        ...ELK_OPTIONS,
        'elk.padding': `[top=${GROUP_PADDING},left=${GROUP_PADDING},bottom=${GROUP_PADDING},right=${GROUP_PADDING}]`,
      };

      // Add internal edges (child_edges)
      if (node.child_edges && node.child_edges.length > 0) {
        elkNode.edges = node.child_edges.map((edge: ChildEdge, idx: number) => ({
          id: `internal-${node.node_id}-${idx}`,
          sources: [edge.source_id],
          targets: [edge.target_id],
        }));
      } else if (node.children.length > 1) {
        // Create sequential edges if no explicit edges defined
        elkNode.edges = node.children.slice(0, -1).map((child, idx) => ({
          id: `seq-${node.node_id}-${idx}`,
          sources: [child.node_id],
          targets: [node.children![idx + 1].node_id],
        }));
      }
    }

    return elkNode;
  }

  // Build root graph
  const rootGraph: ElkNode = {
    id: 'root',
    layoutOptions: ELK_OPTIONS,
    children: nodes.map(convertNode),
    edges: edges.map((edge, idx) => ({
      id: `edge-${idx}`,
      sources: [edge.source_node_id],
      targets: [edge.target_node_id],
    })),
  };

  return rootGraph;
}

/**
 * Extract positions from ELK layout result.
 * Handles nested nodes by computing absolute positions.
 */
function extractPositions(
  elkGraph: ElkNode,
  parentX: number = 0,
  parentY: number = 0,
  parentMap: Record<string, string | undefined> = {},
  currentParentId?: string
): { positions: Record<string, { x: number; y: number }>; dimensions: Record<string, { width: number; height: number }> } {
  const positions: Record<string, { x: number; y: number }> = {};
  const dimensions: Record<string, { width: number; height: number }> = {};

  elkGraph.children?.forEach((child: ElkNode) => {
    const x = (child.x || 0) + parentX;
    const y = (child.y || 0) + parentY;
    const width = child.width || NODE_WIDTH;
    const height = child.height || NODE_HEIGHT;

    positions[child.id] = { x, y };
    dimensions[child.id] = { width, height };
    parentMap[child.id] = currentParentId;

    // Recursively process nested children
    if (child.children && child.children.length > 0) {
      const nested = extractPositions(child, x, y, parentMap, child.id);
      Object.assign(positions, nested.positions);
      Object.assign(dimensions, nested.dimensions);
    }
  });

  return { positions, dimensions };
}

/**
 * Calculate layout using ELK.js for compound/hierarchical graphs.
 * Returns positions, dimensions, and parent mapping for all nodes.
 */
export async function calculateElkLayout(
  nodes: LogicalNode[],
  edges: LogicalEdge[]
): Promise<LayoutResult> {
  if (nodes.length === 0) {
    return { positions: {}, dimensions: {}, parentMap: {} };
  }

  try {
    const elkGraph = buildElkGraph(nodes, edges);
    const layoutedGraph = await elk.layout(elkGraph);

    const parentMap: Record<string, string | undefined> = {};
    const { positions, dimensions } = extractPositions(layoutedGraph, 0, 0, parentMap);

    return { positions, dimensions, parentMap };
  } catch (error) {
    console.error('ELK layout error:', error);
    // Fallback to simple grid layout
    return fallbackLayout(nodes, edges);
  }
}

/**
 * Fallback layout when ELK fails.
 * Simple top-to-bottom grid layout.
 */
function fallbackLayout(
  nodes: LogicalNode[],
  edges: LogicalEdge[]
): LayoutResult {
  const positions: Record<string, { x: number; y: number }> = {};
  const dimensions: Record<string, { width: number; height: number }> = {};
  const parentMap: Record<string, string | undefined> = {};

  const flatNodes = flattenHierarchy(nodes);
  const SPACING_X = 320;
  const SPACING_Y = 180;

  // Group by depth
  const byDepth: Record<number, FlattenedNode[]> = {};
  flatNodes.forEach((fn) => {
    if (!byDepth[fn.depth]) byDepth[fn.depth] = [];
    byDepth[fn.depth].push(fn);
  });

  // Position each level
  Object.entries(byDepth).forEach(([depthStr, nodesAtDepth]) => {
    const depth = parseInt(depthStr);
    nodesAtDepth.forEach((fn, idx) => {
      positions[fn.node.node_id] = {
        x: idx * SPACING_X,
        y: depth * SPACING_Y,
      };
      dimensions[fn.node.node_id] = {
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
      };
      parentMap[fn.node.node_id] = fn.parentId;
    });
  });

  return { positions, dimensions, parentMap };
}

/**
 * Get all edges including internal child edges.
 * Flattens the edge structure for React Flow.
 */
export function getAllEdges(
  nodes: LogicalNode[],
  edges: LogicalEdge[]
): { source: string; target: string; isInternal: boolean }[] {
  const result: { source: string; target: string; isInternal: boolean }[] = [];

  // Add top-level edges (spread extra fields like label, edge_type from enriched edges)
  edges.forEach((edge) => {
    result.push({
      source: edge.source_node_id,
      target: edge.target_node_id,
      isInternal: false,
      ...('label' in edge ? { label: (edge as any).label } : {}),
      ...('edge_type' in edge ? { edge_type: (edge as any).edge_type } : {}),
    });
  });

  // Recursively collect internal edges
  function collectInternalEdges(node: LogicalNode) {
    if (node.children && node.children.length > 0) {
      // Add explicit child edges
      if (node.child_edges && node.child_edges.length > 0) {
        node.child_edges.forEach((edge) => {
          result.push({
            source: edge.source_id,
            target: edge.target_id,
            isInternal: true,
          });
        });
      } else {
        // Create sequential edges if no explicit edges
        node.children.slice(0, -1).forEach((child, idx) => {
          result.push({
            source: child.node_id,
            target: node.children![idx + 1].node_id,
            isInternal: true,
          });
        });
      }

      // Recurse into children
      node.children.forEach(collectInternalEdges);
    }
  }

  nodes.forEach(collectInternalEdges);

  return result;
}

/**
 * Flatten all nodes into a flat array with parent references.
 * Used for creating React Flow nodes with parentId.
 */
export function flattenAllNodes(nodes: LogicalNode[]): FlattenedNode[] {
  return flattenHierarchy(nodes);
}

// =============================================================================
// FLAT FORMAT FUNCTIONS (ProcessNode with parent_id)
// =============================================================================

/**
 * Compute depth for each node in flat format by walking parent_id chain.
 */
function computeDepthMap(nodes: ProcessNode[]): Map<string, number> {
  const nodeMap = new Map<string, ProcessNode>();
  nodes.forEach(n => nodeMap.set(n.node_id, n));

  const depthCache = new Map<string, number>();

  function getDepth(nodeId: string): number {
    if (depthCache.has(nodeId)) {
      return depthCache.get(nodeId)!;
    }

    const node = nodeMap.get(nodeId);
    if (!node || node.parent_id === null) {
      depthCache.set(nodeId, 0);
      return 0;
    }

    const depth = getDepth(node.parent_id) + 1;
    depthCache.set(nodeId, depth);
    return depth;
  }

  nodes.forEach(n => getDepth(n.node_id));
  return depthCache;
}

/**
 * Build ELK graph structure from flat ProcessNodes.
 * Reconstructs hierarchy from parent_id references.
 */
function buildElkGraphFromFlat(
  nodes: ProcessNode[],
  edges: ProcessEdge[]
): ElkNode {
  const nodeMap = new Map<string, ProcessNode>();
  nodes.forEach(n => nodeMap.set(n.node_id, n));

  // Build a map of parent -> children
  const childrenMap = new Map<string | null, ProcessNode[]>();
  nodes.forEach(node => {
    const parentId = node.parent_id;
    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, []);
    }
    childrenMap.get(parentId)!.push(node);
  });

  // Recursive function to convert ProcessNode to ElkNode
  // Uses minimal dimensions for the new GraphNode design
  function convertNode(node: ProcessNode): ElkNode {
    const elkNode: ElkNode = {
      id: node.node_id,
      width: MINIMAL_NODE_WIDTH,
      height: MINIMAL_NODE_HEIGHT,
    };

    // Check if this node has children
    const children = childrenMap.get(node.node_id) || [];

    if (children.length > 0) {
      elkNode.children = children.map(convertNode);
      // Use vertical (DOWN) layout for children inside groups
      // This creates a more compact layout when groups are expanded
      elkNode.layoutOptions = {
        ...ELK_OPTIONS_GROUP_CHILDREN,
        'elk.padding': `[top=${GROUP_PADDING},left=${GROUP_PADDING},bottom=${GROUP_PADDING},right=${GROUP_PADDING}]`,
      };

      // Find edges internal to this group (both source and target are descendants)
      const descendantIds = new Set<string>();
      const collectDescendants = (n: ProcessNode) => {
        descendantIds.add(n.node_id);
        (childrenMap.get(n.node_id) || []).forEach(collectDescendants);
      };
      children.forEach(collectDescendants);

      const internalEdges = edges.filter(
        e => descendantIds.has(e.source_node_id) && descendantIds.has(e.target_node_id)
      );

      if (internalEdges.length > 0) {
        elkNode.edges = internalEdges.map((edge, idx) => ({
          id: `internal-${node.node_id}-${idx}`,
          sources: [edge.source_node_id],
          targets: [edge.target_node_id],
        }));
      } else if (children.length > 1) {
        // Create sequential edges if no explicit edges defined
        elkNode.edges = children.slice(0, -1).map((child, idx) => ({
          id: `seq-${node.node_id}-${idx}`,
          sources: [child.node_id],
          targets: [children[idx + 1].node_id],
        }));
      }
    }

    return elkNode;
  }

  // Get root nodes (parent_id is null)
  let rootNodes = childrenMap.get(null) || [];

  // Fallback: if no explicit root nodes, treat all nodes as roots
  // This handles cases where data doesn't have proper parent_id structure
  if (rootNodes.length === 0) {
    console.warn('[ELK] No root nodes found (parent_id === null). Using all nodes as roots.');
    rootNodes = nodes;
  }

  console.log(`[ELK] Building graph: ${rootNodes.length} root nodes, ${nodes.length} total nodes, ${edges.length} edges`);

  // Get all edges for proper layout (ELK needs all edges to determine flow)
  // Include all edges, not just top-level ones
  const allEdges = edges;

  // Build root graph with minimal layout options
  const rootGraph: ElkNode = {
    id: 'root',
    layoutOptions: ELK_OPTIONS_MINIMAL,
    children: rootNodes.map(convertNode),
    edges: allEdges.map((edge, idx) => ({
      id: `edge-${idx}`,
      sources: [edge.source_node_id],
      targets: [edge.target_node_id],
    })),
  };

  return rootGraph;
}

/**
 * Calculate layout using ELK.js for flat ProcessNode format.
 */
export async function calculateElkLayoutFlat(
  nodes: ProcessNode[],
  edges: ProcessEdge[]
): Promise<LayoutResult> {
  if (nodes.length === 0) {
    return { positions: {}, dimensions: {}, parentMap: {} };
  }

  try {
    const elkGraph = buildElkGraphFromFlat(nodes, edges);
    const layoutedGraph = await elk.layout(elkGraph);

    const parentMap: Record<string, string | undefined> = {};
    // Build parentMap from flat nodes
    nodes.forEach(n => {
      parentMap[n.node_id] = n.parent_id || undefined;
    });

    const { positions, dimensions } = extractPositions(layoutedGraph, 0, 0, parentMap);

    console.log(`[ELK] Layout complete: ${Object.keys(positions).length} positions computed`);
    if (Object.keys(positions).length > 0) {
      console.log('[ELK] Sample positions:', Object.entries(positions).slice(0, 3));
    }

    return { positions, dimensions, parentMap };
  } catch (error) {
    console.error('ELK layout error (flat):', error);
    return fallbackLayoutFlat(nodes, edges);
  }
}

/**
 * Fallback layout for flat format when ELK fails.
 * Uses edge-based ordering for clear left-to-right flow.
 */
function fallbackLayoutFlat(
  nodes: ProcessNode[],
  edges: ProcessEdge[]
): LayoutResult {
  const positions: Record<string, { x: number; y: number }> = {};
  const dimensions: Record<string, { width: number; height: number }> = {};
  const parentMap: Record<string, string | undefined> = {};

  const SPACING_X = 200;  // Horizontal spacing between layers
  const SPACING_Y = 80;   // Vertical spacing within layer

  // Build adjacency for topological ordering
  const nodeMap = new Map<string, ProcessNode>();
  nodes.forEach(n => nodeMap.set(n.node_id, n));

  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();
  const inDegree = new Map<string, number>();

  nodes.forEach(n => {
    outgoing.set(n.node_id, []);
    incoming.set(n.node_id, []);
    inDegree.set(n.node_id, 0);
  });

  edges.forEach(e => {
    outgoing.get(e.source_node_id)?.push(e.target_node_id);
    incoming.get(e.target_node_id)?.push(e.source_node_id);
    inDegree.set(e.target_node_id, (inDegree.get(e.target_node_id) || 0) + 1);
  });

  // Compute layer for each node using BFS from roots
  const layerMap = new Map<string, number>();
  const queue: string[] = [];

  // Find roots (nodes with no incoming edges)
  nodes.forEach(n => {
    if ((inDegree.get(n.node_id) || 0) === 0) {
      queue.push(n.node_id);
      layerMap.set(n.node_id, 0);
    }
  });

  // BFS to assign layers
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    const layer = layerMap.get(nodeId) || 0;

    for (const targetId of outgoing.get(nodeId) || []) {
      const currentLayer = layerMap.get(targetId);
      if (currentLayer === undefined || currentLayer < layer + 1) {
        layerMap.set(targetId, layer + 1);
      }
      // Only process when all predecessors are done
      const newInDegree = (inDegree.get(targetId) || 1) - 1;
      inDegree.set(targetId, newInDegree);
      if (newInDegree === 0) {
        queue.push(targetId);
      }
    }
  }

  // Handle disconnected nodes (no edges) - use parent_id depth
  nodes.forEach(n => {
    if (!layerMap.has(n.node_id)) {
      const depthMap = computeDepthMap(nodes);
      layerMap.set(n.node_id, depthMap.get(n.node_id) || 0);
    }
  });

  // Group by layer
  const byLayer: Record<number, ProcessNode[]> = {};
  nodes.forEach(node => {
    const layer = layerMap.get(node.node_id) || 0;
    if (!byLayer[layer]) byLayer[layer] = [];
    byLayer[layer].push(node);
  });

  // Position each layer - HORIZONTAL flow (x = layer, y = index)
  Object.entries(byLayer).forEach(([layerStr, nodesInLayer]) => {
    const layer = parseInt(layerStr);
    // Center vertically
    const totalHeight = (nodesInLayer.length - 1) * SPACING_Y;
    const startY = -totalHeight / 2;

    nodesInLayer.forEach((node, idx) => {
      positions[node.node_id] = {
        x: layer * SPACING_X,
        y: startY + idx * SPACING_Y,
      };
      dimensions[node.node_id] = {
        width: MINIMAL_NODE_WIDTH,
        height: MINIMAL_NODE_HEIGHT,
      };
      parentMap[node.node_id] = node.parent_id || undefined;
    });
  });

  return { positions, dimensions, parentMap };
}

/**
 * Get all edges from flat format.
 * Returns explicit logical edges PLUS implicit hierarchy edges.
 * Hierarchy edges connect parent groups to their children for visual flow.
 */
export function getAllEdgesFromFlat(
  nodes: ProcessNode[],
  edges: ProcessEdge[]
): { source: string; target: string; isInternal: boolean; isHierarchy?: boolean }[] {
  const result: { source: string; target: string; isInternal: boolean; isHierarchy?: boolean }[] = [];

  // Build parent lookup and children map
  const parentMap = new Map<string, string | null>();
  const childrenMap = new Map<string, ProcessNode[]>();

  nodes.forEach(n => {
    parentMap.set(n.node_id, n.parent_id);

    // Build children map
    const parentId = n.parent_id;
    if (parentId) {
      if (!childrenMap.has(parentId)) {
        childrenMap.set(parentId, []);
      }
      childrenMap.get(parentId)!.push(n);
    }
  });

  // Track which nodes are already connected by explicit edges
  const nodesInEdges = new Set<string>();
  edges.forEach(edge => {
    nodesInEdges.add(edge.source_node_id);
    nodesInEdges.add(edge.target_node_id);
  });

  // Add explicit logical edges (the reasoning flow)
  edges.forEach(edge => {
    const sourceParent = parentMap.get(edge.source_node_id);
    const targetParent = parentMap.get(edge.target_node_id);

    // Edge is internal if both nodes share the same parent
    const isInternal = sourceParent !== null && sourceParent === targetParent;

    result.push({
      source: edge.source_node_id,
      target: edge.target_node_id,
      isInternal,
    });
  });

  // Add implicit hierarchy edges for orphan group nodes
  // Connect parent → first child for each group that has no edges
  nodes.forEach(node => {
    const isGroup = node.node_type === 'group';
    const hasEdges = nodesInEdges.has(node.node_id);
    const children = childrenMap.get(node.node_id) || [];

    // If this node is an orphan (not in any edge) and has children, add hierarchy edge
    if (!hasEdges && children.length > 0) {
      // Connect to first child (or all children for groups)
      const firstChild = children[0];
      result.push({
        source: node.node_id,
        target: firstChild.node_id,
        isInternal: false,
        isHierarchy: true,
      });
    }

    // Also connect sibling groups (children of same parent) if they're orphaned
    if (isGroup && node.parent_id) {
      const siblings = childrenMap.get(node.parent_id) || [];
      const myIndex = siblings.findIndex(s => s.node_id === node.node_id);

      // If this group has no outgoing edges and there's a next sibling, connect to it
      if (!nodesInEdges.has(node.node_id) && myIndex >= 0 && myIndex < siblings.length - 1) {
        const nextSibling = siblings[myIndex + 1];
        // Only add if next sibling is also a group and also orphaned
        if (nextSibling.node_type === 'group' && !nodesInEdges.has(nextSibling.node_id)) {
          result.push({
            source: node.node_id,
            target: nextSibling.node_id,
            isInternal: false,
            isHierarchy: true,
          });
        }
      }
    }
  });

  console.log(`[getAllEdgesFromFlat] ${edges.length} explicit + ${result.length - edges.length} hierarchy = ${result.length} total edges`);

  return result;
}

/**
 * Flatten flat ProcessNodes into array with depth info.
 * Used for creating React Flow nodes with parentId.
 */
export function flattenProcessNodes(nodes: ProcessNode[]): FlattenedProcessNode[] {
  const depthMap = computeDepthMap(nodes);

  return nodes.map(node => ({
    node,
    parentId: node.parent_id,
    depth: depthMap.get(node.node_id) || 0,
  }));
}

/**
 * Count children for a node in flat format.
 */
export function countChildrenFlat(nodes: ProcessNode[], nodeId: string): number {
  let count = 0;

  function countDescendants(parentId: string) {
    const children = nodes.filter(n => n.parent_id === parentId);
    count += children.length;
    children.forEach(child => countDescendants(child.node_id));
  }

  countDescendants(nodeId);
  return count;
}
